/**
 * Module kernel — contract layer (Ultra §2, operator ruling 6).
 *
 * A module is a complete vertical capability: typed data/config schemas,
 * pure typed operations, fixed static views, editor contributions, and an
 * MCP projection — while core owns every security and lifecycle concern.
 *
 * The registry is FIRST-PARTY and FROZEN at startup: no marketplace, no
 * dynamic imports, no module-defined auth/SQL/network/processes. One
 * Veldon release ships the whole registry; sites pin DATA versions.
 */
import { z } from 'zod';
import type { DefModuleMountT } from '../schema/split.js';
import type { JsonValueT } from '../schema/types.js';

/** A pure, typed domain operation over module data (service wires authz/audit). */
export interface ModuleOperation<TData> {
  /** Operation input schema (also the MCP tool projection). */
  input: z.ZodType;
  /** Pure transform: (data, input) -> new data. Throws ModuleOpError on invalid input. */
  apply(data: TData, input: unknown): TData;
  /** One-line description surfaced to editors and agents. */
  describe: string;
}

/** A fixed static view: module data -> section html (+ optional css). */
export interface ModuleView<TData, TConfig = unknown> {
  render(mount: DefModuleMountT, data: TData, config?: TConfig): { html: string; css?: string };
}

export interface FirstPartyModule<TData = unknown, TConfig = unknown> {
  id: string;
  contractVersion: 1;
  /** Current data version; stored data below this migrates on read/publish. */
  dataVersion: number;
  dataSchema: z.ZodType<TData>;
  configSchema: z.ZodType<TConfig>;
  /**
   * OPTIONAL schema for a module's SERVER-SIDE-ONLY per-instance config — the
   * pieces a module's public dataSchema/configSchema deliberately exclude
   * (e.g. forms.contact's recipient email, allowed origins, Turnstile secret
   * ref, retention). Stored in `site_modules.config_json` (SiteModulesRepository),
   * resolved by the RUNTIME only — never part of ContentSnapshot, never
   * readable by editing surfaces or agents. A module with no server-side
   * config (e.g. venue.menu-hours) omits this field entirely.
   */
  privateConfigSchema?: z.ZodType;
  operations: Record<string, ModuleOperation<TData>>;
  views: Record<string, ModuleView<TData, TConfig>>;
  /** Pure migration for stored data at an older version. */
  migrate: (fromVersion: number, data: JsonValueT) => TData;
}

export class ModuleOpError extends Error {
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.code = code;
  }
}

/**
 * Reserved path prefixes no MODULE-GENERATED ROUTE may claim (enforced when
 * route generation lands with the portfolio module). This is NOT applied to
 * module config paths like a form's same-origin submit endpoint, which
 * legitimately live under /api.
 */
export const PROTECTED_PATH_PREFIXES = ['/admin', '/api', '/edit', '/.well-known', '/mcp'] as const;

/**
 * Migration for a single-version module (only dataVersion 1 exists): reject
 * anything below/above 1, else validate against the current data schema.
 * Shared so both shipping modules can't drift on this boilerplate.
 */
export function identityMigration<TData>(moduleId: string, dataSchema: z.ZodType<TData>) {
  return (fromVersion: number, data: JsonValueT): TData => {
    if (fromVersion < 1) throw new ModuleOpError('unmigratable', `cannot migrate ${moduleId} data from version ${fromVersion}`);
    if (fromVersion > 1) throw new ModuleOpError('data-version-ahead', `${moduleId} data version ${fromVersion} is newer than 1`);
    const parsed = dataSchema.safeParse(data);
    if (!parsed.success) throw new ModuleOpError('data-invalid', parsed.error.issues[0]?.message ?? `invalid ${moduleId} data`);
    return parsed.data;
  };
}

export interface ResolveIssue {
  code:
    | 'unknown-module'
    | 'unknown-view'
    | 'missing-module-data'
    | 'data-version-ahead'
    | 'data-invalid';
  where: string;
  message: string;
}

interface ResolvedModule {
  module: FirstPartyModule;
  view: ModuleView<unknown, unknown>;
  data: unknown;
  config: unknown;
}

function isResolveIssue(value: ResolvedModule | ResolveIssue): value is ResolveIssue {
  return 'code' in value;
}

/**
 * PURE data-resolution half of a mount: registry lookup, own-property view
 * lookup (so `view: "constructor"` can't reach Object.prototype), config
 * validation, migrate-on-read, and data-schema validation. No rendering —
 * this is what the snapshot validator calls to pre-flight every mount, and
 * what resolveMount builds on. Returns the validated pieces or a ResolveIssue.
 */
export function resolveModuleData(
  registry: ReadonlyMap<string, FirstPartyModule>,
  mount: DefModuleMountT,
  stored: { dataVersion: number; data: JsonValueT } | undefined,
): ResolvedModule | ResolveIssue {
  const module = registry.get(mount.moduleId);
  if (!module) return { code: 'unknown-module', where: mount.id, message: `module "${mount.moduleId}" is not in the registry` };
  if (!Object.hasOwn(module.views, mount.view)) {
    return { code: 'unknown-view', where: mount.id, message: `module "${mount.moduleId}" has no view "${mount.view}"` };
  }
  const view = module.views[mount.view]!;
  const configResult = module.configSchema.safeParse(mount.config ?? {});
  if (!configResult.success) {
    return { code: 'data-invalid', where: mount.id, message: `invalid module config: ${configResult.error.issues[0]?.message ?? 'invalid'}` };
  }
  if (!stored) return { code: 'missing-module-data', where: mount.id, message: `no module data for instance "${mount.instanceId}"` };
  if (stored.dataVersion > module.dataVersion) {
    return { code: 'data-version-ahead', where: mount.id, message: `data version ${stored.dataVersion} is newer than registry ${module.dataVersion}` };
  }
  const migrated = stored.dataVersion < module.dataVersion ? module.migrate(stored.dataVersion, stored.data) : stored.data;
  const parsed = module.dataSchema.safeParse(migrated);
  if (!parsed.success) {
    return { code: 'data-invalid', where: mount.id, message: parsed.error.issues[0]?.message ?? 'invalid module data' };
  }
  return { module, view, data: parsed.data, config: configResult.data };
}

/**
 * Resolve a mount to rendered view output. Older data migrates; data NEWER
 * than the registry fails (never render content the code does not understand).
 */
export function resolveMount(
  registry: ReadonlyMap<string, FirstPartyModule>,
  mount: DefModuleMountT,
  stored: { dataVersion: number; data: JsonValueT } | undefined,
): { html: string; css?: string } | ResolveIssue {
  const resolved = resolveModuleData(registry, mount, stored);
  if (isResolveIssue(resolved)) return resolved;
  return resolved.view.render(mount, resolved.data, resolved.config);
}
